import FlexRow from './FlexRow'
import FlexCol from './FlexCol'
import { EmptySwapList } from './Empty'
import { FlowContext } from '../providers/flow'
import { ConfigContext } from '../providers/config'
import Text, { TextLabel, TextSecondary } from './Text'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useContext, useEffect, useRef, useState } from 'react'
import { SwapsContext } from '../providers/swaps'
import { NavigationContext, Pages } from '../providers/navigation'
import { prettyAgo, prettyAmount, prettyDate, prettyHide } from '../lib/format'
import { SwapFailedIcon, SwapPendingIcon, SwapSuccessIcon } from '../icons/Swap'
import { BoltzSwapStatus, BoltzSwap } from '@arkade-os/boltz-swap'
import { consoleError } from '../lib/logs'
import Focusable from './Focusable'

const border = '1px solid var(--neutral-200)'

type statusUI = 'Successful' | 'Pending' | 'Failed' | 'Refunded'

const statusDict = {
  'invoice.expired': 'Failed',
  'invoice.failedToPay': 'Failed',
  'invoice.paid': 'Successful',
  'invoice.pending': 'Pending',
  'invoice.set': 'Pending',
  'invoice.settled': 'Successful',
  'swap.created': 'Pending',
  'swap.expired': 'Failed',
  'transaction.claim.pending': 'Pending',
  'transaction.claimed': 'Successful',
  'transaction.confirmed': 'Successful',
  'transaction.failed': 'Failed',
  'transaction.lockupFailed': 'Failed',
  'transaction.mempool': 'Pending',
  'transaction.refunded': 'Refunded',
  'transaction.server.mempool': 'Pending',
  'transaction.server.confirmed': 'Pending',
} satisfies Record<BoltzSwapStatus, statusUI>

const colorDict: Record<statusUI, string> = {
  Failed: 'red',
  Successful: 'green',
  Pending: 'yellow',
  Refunded: 'neutral-500',
}

const iconDict: Record<statusUI, JSX.Element> = {
  Failed: <SwapFailedIcon />,
  Successful: <SwapSuccessIcon />,
  Pending: <SwapPendingIcon />,
  Refunded: <SwapFailedIcon />,
}

const SwapLine = ({ onClick, swap }: { onClick: () => void; swap: BoltzSwap }) => {
  const { config } = useContext(ConfigContext)

  let sats = 0,
    direction = '',
    prefix = ''

  if (swap.type === 'reverse') {
    sats = swap.response.onchainAmount ?? 0
    direction = 'Lightning to Arkade'
    prefix = '+'
  } else if (swap.type === 'submarine') {
    sats = swap.response.expectedAmount
    direction = 'Arkade to Lightning'
    prefix = '-'
  } else if (swap.type === 'chain') {
    sats = swap.request.userLockAmount || swap.request.serverLockAmount || 0
    if (swap.request.from === 'ARK') {
      direction = 'Arkade to Bitcoin'
      prefix = '-'
    } else {
      direction = 'Bitcoin to Arkade'
      prefix = '+'
    }
  }

  if (!direction || !prefix) throw new Error('Invalid swap data')

  const status: statusUI = statusDict[swap.status as BoltzSwapStatus] || 'Pending'
  const amount = `${prefix} ${config.showBalance ? prettyAmount(sats) : prettyHide(sats)}`
  const when = window.innerWidth < 400 ? prettyAgo(swap.createdAt) : prettyDate(swap.createdAt)
  const refunded = swap.type === 'submarine' && swap.refunded
  const color = refunded ? colorDict['Refunded'] : colorDict[status]

  const Icon = iconDict[status]
  const Kind = () => <Text thin>{direction}</Text>
  const When = () => <TextSecondary>{when}</TextSecondary>
  const Sats = () => <Text color={color}>{amount}</Text>
  const Stat = () => <Text color={color}>{refunded ? 'Refunded' : status}</Text>

  const rowStyle = {
    alignItems: 'center',
    borderTop: border,
    cursor: 'pointer',
    padding: '0.5rem 0',
  }

  return (
    <div style={rowStyle} onClick={onClick}>
      <FlexRow>
        <FlexRow>
          {Icon}
          <div>
            <Kind />
            <Sats />
          </div>
        </FlexRow>
        <FlexCol gap='0' end>
          <Stat />
          <When />
        </FlexCol>
      </FlexRow>
    </div>
  )
}

export default function SwapsList() {
  const { setSwapInfo } = useContext(FlowContext)
  const { navigate } = useContext(NavigationContext)
  const { swapManager, getSwapHistory } = useContext(SwapsContext)

  const [focused, setFocused] = useState(false)
  const [swapHistory, setSwapHistory] = useState<BoltzSwap[]>([])

  const parentRef = useRef<HTMLDivElement>(null)

  // Load initial swap history
  useEffect(() => {
    getSwapHistory()
      .then(setSwapHistory)
      .catch((err) => {
        consoleError(err, 'Error fetching swap history:')
      })
  }, [getSwapHistory])

  // Subscribe to swap updates from SwapManager for real-time updates
  useEffect(() => {
    if (!swapManager) return

    let unsub: (() => void) | null = null
    let cancelled = false
    swapManager
      .onSwapUpdate((swap) => {
        setSwapHistory((prev) => {
          const existingIndex = prev.findIndex((s) => s.id === swap.id)
          if (existingIndex >= 0) {
            const updated = [...prev]
            updated[existingIndex] = swap
            return updated
          }
          // New swap, add to beginning
          return [swap, ...prev]
        })
      })
      .then((unsubscribe) => {
        if (cancelled) {
          unsubscribe()
        } else {
          unsub = unsubscribe
        }
      })

    return () => {
      cancelled = true
      unsub?.()
    }
  }, [swapManager])

  const virtualizer = useVirtualizer({
    count: swapHistory.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 61,
    overscan: 5,
  })

  if (swapHistory.length === 0) return <EmptySwapList />

  const focusOnFirstRow = () => {
    setFocused(true)
    if (swapHistory.length === 0) return
    const id = key(swapHistory[0])
    const first = document.getElementById(id) as HTMLElement
    if (first) first.focus()
  }

  const focusOnOuterShell = () => {
    setFocused(false)
    const outer = document.getElementById('outer') as HTMLElement
    if (outer) outer.focus()
  }

  const ariaLabel = (swap?: BoltzSwap) => {
    if (!swap) return 'Pressing Enter enables keyboard navigation of the swap list'
    return `Transaction ${swap.type} with status ${swap.status}. Press Escape to exit keyboard navigation.`
  }

  const handleClick = (swap: BoltzSwap) => {
    setSwapInfo(swap)
    navigate(Pages.AppBoltzSwap)
  }

  const key = (swap: BoltzSwap) => swap.response.id

  return (
    <div style={{ width: '100%' }}>
      <TextLabel>Swap history</TextLabel>
      <Focusable id='outer' inactive={focused} onEnter={focusOnFirstRow} ariaLabel={ariaLabel()}>
        <div
          ref={parentRef}
          className='hide-scrollbar scroll-fade'
          style={{
            borderBottom: border,
            height: 'calc(100dvh - 280px)',
            minHeight: '200px',
            overflowY: 'auto',
          }}
        >
          <div style={{ height: `${virtualizer.getTotalSize()}px`, position: 'relative', width: '100%' }}>
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const swap = swapHistory[virtualItem.index]
              return (
                <div
                  key={key(swap)}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  <Focusable
                    id={key(swap)}
                    inactive={!focused}
                    ariaLabel={ariaLabel(swap)}
                    onEscape={focusOnOuterShell}
                    onEnter={() => handleClick(swap)}
                  >
                    <SwapLine onClick={() => handleClick(swap)} swap={swap} />
                  </Focusable>
                </div>
              )
            })}
          </div>
        </div>
      </Focusable>
    </div>
  )
}
